import {
  ExcelPreviewColumn,
  ExcelPreviewResult,
  ExcelPreviewRow,
} from '../../_services/operation-requirement.service';
import {
  ShippingConfig,
  ShippingConfigFormData,
  ShippingConfigType,
  versandCellShipped,
} from '../../_models/shipping-config.interface';

// Versandtypen, die Staging-Artikel mit abdecken (Kunden-Rueckversand NICHT) —
// identisch zur Backend-Konstante ShippingConfig::COVERING_TYPES und zur
// Ticket-Matrix.
export const COVERING_TYPES: ShippingConfigType[] = ['technician_shipping', 'store_shipping', 'pickpoint'];

/** Eine Zeile im Inline-Split-Grid: die Auftragsbedarfs-Zeile („need", oben,
 *  read-only), eine konkrete (bestehende oder pending) Config eines Auftrags
 *  oder die „offen"-Summenzeile. */
export interface SplitGridRow {
  rowId: string;
  operationId: number;
  kind: 'config' | 'open' | 'need';
  /** Bei kind==='config': Pending-Key (`${opId}#${configId|newN}`) bzw. die
   *  bestehende config_id als String. Leer bei der offen-Zeile. */
  configRef: string;
  isFirstOfOp: boolean;
  /**
   * Letzte Grid-Zeile dieses Auftrags. Alle anderen bekommen keine
   * Trennlinie nach unten, damit die Zeilen eines Auftrags wie ein Block
   * wirken — so wie die verbundenen Zellen in der Excel.
   */
  isLastOfOp?: boolean;
  /** Referenz auf die Preview-Zeile des Auftrags (Order-Nr, Store, Form-Meta). */
  row: ExcelPreviewRow;
}

/** Spalten-Metadaten fuer die Artikel-Spalten des Grids. */
export interface SplitColumn {
  colId: string;
  label: string;
  isStaging: boolean;
}

/**
 * Kapselt die Mengen-Split-Logik des Bulk-Excel-Inline-Grids: Pool je Artikel,
 * Lock-Floors aus bereits versandten/zurueckgehaltenen Mengen, Staging-nur-auf-
 * deckende-Configs, sowie Auf-/Umverteilung. Haelt KEINEN Angular-State — die
 * Komponente uebergibt den (immutablen) splitState und bekommt einen neuen
 * zurueck, damit Signals sauber triggern.
 *
 * splitState-Form: opId → configRef → colId → quantity.
 */
export class BulkExcelSplitModel {
  constructor(private result: ExcelPreviewResult) {}

  get rows(): ExcelPreviewRow[] {
    return this.result.preview_rows;
  }

  // --- Spalten ---------------------------------------------------------------

  columns(): SplitColumn[] {
    return this.result.preview_columns.map((c: ExcelPreviewColumn) => ({
      colId: c.id,
      label: c.label,
      isStaging: c.kind === 'template' && c.is_staging,
    }));
  }

  // operation_requirement_id → col_id fuer einen Auftrag (aus preview.requirements).
  reqColMap(row: ExcelPreviewRow): Map<number, string> {
    const map = new Map<number, string>();
    for (const r of row.requirements ?? []) map.set(r.id, r.col_id);
    return map;
  }

  // --- Configs eines Auftrags ------------------------------------------------

  // Offene (editierbare/splitbare) Configs des Auftrags.
  existingConfigs(row: ExcelPreviewRow): ShippingConfig[] {
    return [...(row.shipping_configs ?? [])].sort((a, b) => a.id - b.id);
  }

  // Vollstaendig abgeschlossene Configs (beide Kanaele versandt) — read-only.
  completedConfigs(row: ExcelPreviewRow): ShippingConfig[] {
    return [...(row.completed_shipping_configs ?? [])].sort((a, b) => a.id - b.id);
  }

  // Offene + abgeschlossene Configs (fuer Ref-Lookups: Typ, Label, Floor, Kanal).
  allConfigs(row: ExcelPreviewRow): ShippingConfig[] {
    return [...this.existingConfigs(row), ...this.completedConfigs(row)];
  }

  isCompleted(row: ExcelPreviewRow, ref: string): boolean {
    return this.completedConfigs(row).some((c) => String(c.id) === ref);
  }

  // pending ist nach dem vollen Pending-Key (`${opId}#${ref}`) gekeyt (wie in der
  // Komponente) — sowohl fuer pending-neue Configs als auch fuer pending-Edits
  // bestehender Configs (dann gewinnt der editierte Typ).
  configType(row: ExcelPreviewRow, ref: string, pending: Map<string, ShippingConfigFormData>): ShippingConfigType | null {
    const p = pending.get(`${row.operation_id}#${ref}`);
    if (p) return p.shipping_type;
    const c = this.allConfigs(row).find((x) => String(x.id) === ref);
    return c?.shipping_type ?? null;
  }

  isCovering(type: ShippingConfigType | null): boolean {
    return type != null && COVERING_TYPES.includes(type);
  }

  // Staging-Spalten nur deckenden Configs zuweisbar, Versand jeder Config.
  canAssign(type: ShippingConfigType | null, col: SplitColumn): boolean {
    return !col.isStaging || this.isCovering(type);
  }

  // --- Lock-Floors (bereits versandt/zurueckgehalten) ------------------------

  // floor je colId fuer eine bestehende Config (pending-neue Configs = 0).
  floorByCol(row: ExcelPreviewRow, ref: string): Map<string, number> {
    const result = new Map<string, number>();
    const config = this.allConfigs(row).find((c) => String(c.id) === ref);
    if (!config) return result;
    const reqCol = this.reqColMap(row);
    for (const e of config.requirement_committed ?? []) {
      const colId = reqCol.get(e.operation_requirement_id);
      if (!colId) continue;
      result.set(colId, (result.get(colId) ?? 0) + e.shipped + e.held);
    }
    return result;
  }

  floorFor(row: ExcelPreviewRow, ref: string, colId: string): number {
    return this.floorByCol(row, ref).get(colId) ?? 0;
  }

  /**
   * Committe Menge einer Config getrennt nach bereits versandt und noch
   * zurueckgehalten. floorFor summiert beides, weil fuer die Untergrenze nur
   * die Summe zaehlt — fuer die Anzeige ist der Unterschied aber genau das,
   * was der Lagerist wissen will ("2 raus, 1 steht noch aus").
   */
  committedSplit(row: ExcelPreviewRow, ref: string, colId: string): { shipped: number; held: number } {
    const config = this.allConfigs(row).find((c) => String(c.id) === ref);
    if (!config) return { shipped: 0, held: 0 };

    const reqCol = this.reqColMap(row);
    let shipped = 0;
    let held = 0;
    for (const e of config.requirement_committed ?? []) {
      if (reqCol.get(e.operation_requirement_id) !== colId) continue;

      shipped += e.shipped;
      held += e.held;
    }
    return { shipped, held };
  }

  // --- Pre-Import-Baseline (fuer das pro-Zeile-Delta „(+X)") -------------------

  // DB-Split (requirement_split) eines Artikels auf einer bestehenden Config —
  // der Stand VOR dem Excel-Import. Pending-neue/abgeschlossene Configs → leer.
  originalSplitByCol(row: ExcelPreviewRow, ref: string): Map<string, number> {
    const result = new Map<string, number>();
    const config = this.existingConfigs(row).find((c) => String(c.id) === ref);
    if (!config) return result;
    const reqCol = this.reqColMap(row);
    for (const s of config.requirement_split ?? []) {
      const colId = reqCol.get(s.operation_requirement_id);
      if (colId) result.set(colId, (result.get(colId) ?? 0) + s.quantity);
    }
    return result;
  }

  originalSplitQty(row: ExcelPreviewRow, ref: string, colId: string): number {
    return this.originalSplitByCol(row, ref).get(colId) ?? 0;
  }

  // Pre-Import nicht zugeordneter Rest eines Artikels (Baseline der offen-Zeile):
  // alte Gesamtmenge minus dem, was schon zugeordnet war — auf offene Configs
  // (requirement_split) UND auf abgeschlossene Configs (requirement_committed,
  // shipped+held). Letzteres ist wichtig, damit bereits versandte Mengen nicht
  // als „unzugeordnet-alt" in die Baseline wandern und das offen-Zeilen-Delta
  // unterzaehlen — der Pool rechnet die abgeschlossenen Mengen via Seeding
  // ohnehin schon raus, also muss die Baseline es symmetrisch tun.
  originalUnassignedQty(row: ExcelPreviewRow, colId: string): number {
    const old = row.cells[colId]?.old_quantity ?? 0;
    let assigned = 0;
    for (const c of this.existingConfigs(row)) {
      assigned += this.originalSplitByCol(row, String(c.id)).get(colId) ?? 0;
    }
    for (const c of this.completedConfigs(row)) {
      assigned += this.floorByCol(row, String(c.id)).get(colId) ?? 0;
    }
    return Math.max(0, old - assigned);
  }

  // Kanal dieser bestehenden Config bereits versandt? (pending-neu = nie).
  channelShipped(row: ExcelPreviewRow, ref: string, col: SplitColumn): boolean {
    const config = this.allConfigs(row).find((c) => String(c.id) === ref);
    if (!config) return false;
    return col.isStaging ? !!config.staging_shipped : !!config.versand_shipped;
  }

  // --- Mengen / Pool ---------------------------------------------------------

  cellQty(state: SplitState, opId: number, ref: string, colId: string): number {
    return state.get(opId)?.get(ref)?.get(colId) ?? 0;
  }

  assignedTotal(state: SplitState, opId: number, colId: string): number {
    let sum = 0;
    const byRef = state.get(opId);
    if (!byRef) return 0;
    for (const inner of byRef.values()) sum += inner.get(colId) ?? 0;
    return sum;
  }

  // Bereits ueber Configs mit versandtem VERSAND-Kanal verschickte Menge eines
  // VERSAND-Artikels — verlaesst den offenen Pool (analog Matrix).
  private versandShippedConsumed(row: ExcelPreviewRow, col: SplitColumn): number {
    if (col.isStaging) return 0;
    let sum = 0;
    for (const c of this.existingConfigs(row)) {
      if (!c.versand_shipped) continue;
      const floor = this.floorByCol(row, String(c.id)).get(col.colId) ?? 0;
      sum += floor;
    }
    return sum;
  }

  // Noch nicht verteilte Menge eines Artikels (steht in der offen-Zeile).
  poolFor(state: SplitState, row: ExcelPreviewRow, col: SplitColumn): number {
    const total = row.cells[col.colId]?.new_quantity ?? 0;
    return Math.max(0, total - this.assignedTotal(state, row.operation_id, col.colId) - this.versandShippedConsumed(row, col));
  }

  // --- Initialisierung -------------------------------------------------------

  // Anfangszustand: bestehende requirement_splits uebernehmen, danach den noch
  // offenen Rest je Artikel greedy der ersten zuweisbaren Config zuordnen
  // (mirrors backend link_new_requirement_to_config — „offen" bleibt 0 im
  // Regelfall mit genau einer passenden Config).
  initialState(pending: Map<string, ShippingConfigFormData>): SplitState {
    const state: SplitState = new Map();
    const cols = this.columns();

    const colById = new Map(cols.map((c) => [c.colId, c]));

    for (const row of this.rows) {
      const byRef = new Map<string, Map<string, number>>();
      const reqCol = this.reqColMap(row);

      for (const c of this.existingConfigs(row)) {
        const inner = new Map<string, number>();
        for (const s of c.requirement_split ?? []) {
          const colId = reqCol.get(s.operation_requirement_id);
          if (!colId) continue;
          // Gleiche Seeding-Regel wie die Versand-Matrix (initCellsFrom):
          // versandte VERSAND-Zellen teilversandter Configs NICHT in den State
          // seeden — ihre Menge zählt bereits über versandShippedConsumed als
          // Untergrenze. Mitgeseedet würde sie doppelt abgezogen, der Pool bei
          // unveränderter Excel-Menge negativ und reduceOverhang nähme offenen
          // Geschwister-Configs fälschlich ihre Mengen weg (hawk#558, Aufgabe C).
          const col = colById.get(colId);
          if (col && versandCellShipped(c, col.isStaging)) continue;
          inner.set(colId, (inner.get(colId) ?? 0) + s.quantity);
        }
        byRef.set(String(c.id), inner);
      }

      // Abgeschlossene Configs read-only aus requirement_committed (versandt +
      // gehalten = die tatsaechlich rausgegangene Menge) seeden. Sie zaehlen in
      // assignedTotal → fallen damit aus dem offenen Pool, ohne dass ein extra
      // Pool-Term noetig ist (kein Double-Count mit versandShippedConsumed, das
      // nur offene Configs betrachtet).
      for (const c of this.completedConfigs(row)) {
        const inner = new Map<string, number>();
        for (const e of c.requirement_committed ?? []) {
          const colId = reqCol.get(e.operation_requirement_id);
          if (colId) inner.set(colId, (inner.get(colId) ?? 0) + e.shipped + e.held);
        }
        byRef.set(String(c.id), inner);
      }

      state.set(row.operation_id, byRef);

      // Greedy-Default für den offenen Rest — bzw. beim Verringern der
      // Excel-Menge den Überhang wieder von den Config-Zeilen abbauen. Ohne
      // den Abbau-Zweig blieben die geseedeten Alt-Splits stehen und die
      // Tabelle zeigte weiterhin die alte (höhere) Gesamtmenge, obwohl das
      // Backend bereits die verringerte new_quantity liefert.
      for (const col of cols) {
        const total = row.cells[col.colId]?.new_quantity ?? 0;
        const rawPool = total
          - this.assignedTotal(state, row.operation_id, col.colId)
          - this.versandShippedConsumed(row, col);
        if (rawPool > 0) {
          const ref = this.defaultRefFor(row, col, byRef, pending);
          if (!ref) continue;
          const inner = byRef.get(ref)!;
          inner.set(col.colId, (inner.get(col.colId) ?? 0) + rawPool);
        } else if (rawPool < 0) {
          this.reduceOverhang(row, col, byRef, -rawPool);
        }
      }
    }
    return state;
  }

  // Baut beim Verringern den Überhang (Alt-Split > neue Gesamtmenge) von den
  // editierbaren Config-Zeilen ab — von hinten nach vorn, damit die erste
  // (primäre) Config ihre Zuordnung möglichst behält (Spiegelbild zum
  // Greedy-Add auf die erste Config). Floors aus bereits versandten/gehaltenen
  // Mengen bleiben unangetastet, abgeschlossene Configs sind read-only.
  // Bleibt danach ein Rest (neue Menge unter bereits versandt), zeigt die
  // Backend-Validierung ohnehin einen Zell-Fehler an.
  private reduceOverhang(
    row: ExcelPreviewRow,
    col: SplitColumn,
    byRef: Map<string, Map<string, number>>,
    overhang: number,
  ): void {
    let remaining = overhang;
    for (const ref of [...byRef.keys()].reverse()) {
      if (remaining <= 0) break;
      if (this.isCompleted(row, ref)) continue;
      const inner = byRef.get(ref)!;
      const current = inner.get(col.colId) ?? 0;
      const reducible = Math.max(0, current - this.floorFor(row, ref, col.colId));
      if (reducible <= 0) continue;
      const cut = Math.min(reducible, remaining);
      const next = current - cut;
      if (next <= 0) inner.delete(col.colId);
      else inner.set(col.colId, next);
      remaining -= cut;
    }
  }

  // Erste Config (in Anlege-Reihenfolge), die diese Spalte aufnehmen kann.
  private defaultRefFor(
    row: ExcelPreviewRow,
    col: SplitColumn,
    byRef: Map<string, Map<string, number>>,
    pending: Map<string, ShippingConfigFormData>,
  ): string | null {
    for (const ref of byRef.keys()) {
      // Abgeschlossene/kanal-versendete Configs nehmen keinen offenen Rest mehr auf.
      if (this.channelShipped(row, ref, col)) continue;
      if (this.canAssign(this.configType(row, ref, pending), col)) return ref;
    }
    return null;
  }
}

export type SplitState = Map<number, Map<string, Map<string, number>>>;

// Immutable Setter: liefert einen neuen SplitState mit gesetzter Zelle (oder
// geloeschter, wenn qty<=0) — fuer sauberes Signal-Triggering.
export function withCell(state: SplitState, opId: number, ref: string, colId: string, qty: number): SplitState {
  const next: SplitState = new Map(state);
  const byRef = new Map(next.get(opId) ?? new Map<string, Map<string, number>>());
  const inner = new Map(byRef.get(ref) ?? new Map<string, number>());
  if (qty <= 0) inner.delete(colId);
  else inner.set(colId, qty);
  byRef.set(ref, inner);
  next.set(opId, byRef);
  return next;
}

// Entfernt eine (pending) Config-Spur komplett aus dem State.
export function withoutRef(state: SplitState, opId: number, ref: string): SplitState {
  const next: SplitState = new Map(state);
  const byRef = new Map(next.get(opId) ?? new Map<string, Map<string, number>>());
  byRef.delete(ref);
  next.set(opId, byRef);
  return next;
}
